import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Shield, User, Settings as SettingsIcon, Plus, Save } from 'lucide-react';

const SettingsTab = ({ 
    user,
    users,
    settings,
    setSettings,
    adminRoles,
    canCreateAdminAccounts,
    onCreateAdmin,
    onSaveSettings
}) => {
    return (
        <div className="space-y-6">
            {/* Admin Role Management - Only for Chairman */}
            {canCreateAdminAccounts && (
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <h3 className="font-heading font-bold text-lg uppercase mb-6 flex items-center gap-2">
                        <Shield className="w-5 h-5 text-accent" />
                        Admin Account Management
                    </h3>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Create and manage admin accounts with different access levels.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                            {adminRoles.map((role) => (
                                <div key={role.role} className="bg-[#0a1628] border border-white/10 p-4">
                                    <h4 className="font-bold text-sm mb-1">{role.name}</h4>
                                    <p className="text-xs text-muted-foreground mb-3">{role.description}</p>
                                    <div className="text-xs text-primary">
                                        {users.filter(u => u.role === role.role).length} user(s)
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Button 
                            onClick={onCreateAdmin} 
                            className="bg-accent hover:bg-yellow-400 text-black"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Admin Account
                        </Button>
                    </div>
                </div>
            )}

            {/* Current User Role Info */}
            <div className="bg-[#0d2847] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-lg uppercase mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" />
                    Your Access Level
                </h3>
                <div className="flex items-center gap-4">
                    <div className="px-4 py-2 bg-primary/20 border border-primary/30">
                        <span className="text-primary font-bold uppercase">
                            {adminRoles.find(r => r.role === user?.role)?.name || user?.role}
                        </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                        {user?.role === 'tournament_chairman' || user?.role === 'admin' ? (
                            <span className="text-success">Full Access</span>
                        ) : user?.role === 'tournament_director' ? (
                            <span>All access except create tournament, no import/export</span>
                        ) : user?.role === 'admin_team' ? (
                            <span>All access except create tournament, can import/export</span>
                        ) : (
                            <span>Limited Access</span>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-[#0d2847] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-lg uppercase mb-6 flex items-center gap-2">
                    <SettingsIcon className="w-5 h-5 text-primary" />
                    Platform Settings
                </h3>
                {settings && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label className="text-xs tracking-widest uppercase text-muted-foreground">Platform Name</Label>
                                <Input
                                    value={settings.platform_name || ''}
                                    onChange={(e) => setSettings({...settings, platform_name: e.target.value})}
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs tracking-widest uppercase text-muted-foreground">Tagline</Label>
                                <Input
                                    value={settings.tagline || ''}
                                    onChange={(e) => setSettings({...settings, tagline: e.target.value})}
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label className="text-xs tracking-widest uppercase text-muted-foreground">Max Sports Per Athlete</Label>
                                <Input
                                    type="number"
                                    value={settings.max_sports_per_athlete || 3}
                                    onChange={(e) => setSettings({...settings, max_sports_per_athlete: parseInt(e.target.value)})}
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs tracking-widest uppercase text-muted-foreground">Verification Required</Label>
                                    <button
                                        onClick={() => setSettings({...settings, verification_required: !settings.verification_required})}
                                        className={`w-12 h-6 rounded-full transition-colors ${settings.verification_required ? 'bg-primary' : 'bg-white/20'}`}
                                    >
                                        <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.verification_required ? 'translate-x-6' : 'translate-x-0.5'}`} />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs tracking-widest uppercase text-muted-foreground">Allow Registration</Label>
                                    <button
                                        onClick={() => setSettings({...settings, allow_registration: !settings.allow_registration})}
                                        className={`w-12 h-6 rounded-full transition-colors ${settings.allow_registration ? 'bg-primary' : 'bg-white/20'}`}
                                    >
                                        <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.allow_registration ? 'translate-x-6' : 'translate-x-0.5'}`} />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs tracking-widest uppercase text-muted-foreground">Maintenance Mode</Label>
                                    <button
                                        onClick={() => setSettings({...settings, maintenance_mode: !settings.maintenance_mode})}
                                        className={`w-12 h-6 rounded-full transition-colors ${settings.maintenance_mode ? 'bg-destructive' : 'bg-white/20'}`}
                                    >
                                        <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.maintenance_mode ? 'translate-x-6' : 'translate-x-0.5'}`} />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="pt-4 border-t border-white/5">
                            <Button onClick={onSaveSettings} className="bg-primary hover:bg-cyan-400 text-[#0a1628]">
                                <Save className="w-4 h-4 mr-2" />
                                Save Settings
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SettingsTab;
